// src/components/LocationStateNotice.js
//
// Small, dismissible transparency notice shown the first time the user's
// state is set via automatic location detection (rather than an explicit
// URL/manual choice). This satisfies the requirement that auto-detection is
// never treated as final — the user is told what happened and can change
// their state at any time using the existing Navbar selector.
import React, { useEffect, useState } from 'react';
import { useSelectedState } from '../contexts/SelectedStateContext';
import { STATE_CONTENT } from '../config/stateContent';
import './LocationStateNotice.css';

const DISMISSED_KEY = 'locationStateNoticeDismissed';

export default function LocationStateNotice() {
  const { selectedState, autoDetectedState } = useSelectedState();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === 'true'
  );

  // Auto-hide after a while so it never lingers/obstructs the site.
  useEffect(() => {
    if (!autoDetectedState || dismissed) return undefined;
    const timer = setTimeout(() => handleDismiss(), 10000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDetectedState, dismissed]);

  if (!autoDetectedState || dismissed) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, 'true');
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  const stateName = STATE_CONTENT[selectedState]?.stateName || selectedState;

  return (
    <div className="location-state-notice" role="status" aria-live="polite">
      <span className="location-state-notice__text">
        We set your state to <strong>{stateName}</strong> based on your approximate location.
        Not correct? Use the state selector in the menu to change it.
      </span>
      <button
        type="button"
        className="location-state-notice__close"
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}
